'use client';

import { forwardRef, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from '@packages/shared/src/ui/Button/Button';

export type ResponsiveFlyoutHeaderProps = {
  onClose?: () => void;
  onBack?: () => void;
  title?: string;
  shouldFocusCloseButton?: boolean;
};

const messages = defineMessages({
  back: {
    id: 'header.navigation.flyout.buttons.back',
    defaultMessage: 'Zurück',
  },
  close: {
    id: 'header.navigation.flyout.buttons.close',
    defaultMessage: 'Schließen',
  },
});

/**
 * Header for the navigation popup, displaying the currently selected category, and some controls to navigate back and close the popup.
 */
export const ResponsiveFlyoutHeader = forwardRef<HTMLButtonElement, ResponsiveFlyoutHeaderProps>(
  ({ title, onClose, onBack, shouldFocusCloseButton }, ref) => {
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const backButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      shouldFocusCloseButton && closeButtonRef.current?.focus();
    }, [shouldFocusCloseButton]);

    useEffect(() => {
      const onMainNavHandler = (e: KeyboardEvent) => {
        if (e.key === 'Backspace') onBack?.();
        if (e.key === 'Escape') onClose?.();
      };
      document.addEventListener('keydown', onMainNavHandler);

      return () => {
        document.removeEventListener('keydown', onMainNavHandler);
      };
    }, [onBack, onClose]);

    const { formatMessage } = useIntl();

    return (
      <div className="hidden items-center p-1 [grid-template:'full'_1fr/auto] lg:grid">
        <CSSTransition
          appear
          in={!!onBack}
          timeout={125}
          classNames={{
            enter: 'translate-x-2 opacity-0',
            enterActive: 'translate-x-0 opacity-0',
            enterDone: 'translate-x-0 opacity-100',
            exit: 'translate-x-2 opacity-0',
            exitActive: 'translate-x-2 opacity-0',
          }}
          unmountOnExit
          nodeRef={backButtonRef}
        >
          <Button
            ref={backButtonRef}
            className="start-button typography-h5 col-span-full row-span-full translate-x-2 justify-self-start border-none opacity-0 transition-[opacity,translate] duration-125 ease-in-out"
            onClick={onBack}
          >
            <span className="icon-emp-arrow-medium-left size-4" />
            <span className="text-size-headline-base">{formatMessage(messages.back)}</span>
          </Button>
        </CSSTransition>
        <header className="justify-self-center [grid-area:full]">
          <span className="text-size-headline-xl">{title}</span>
        </header>
        <Button
          className="end-button typography-h5 col-span-full row-span-full justify-self-end border-none"
          ref={closeButtonRef}
          onClick={() => {
            onClose?.();
          }}
        >
          <span className="text-size-headline-base">{formatMessage(messages.close)}</span>
          <span className="icon-emp-cancel size-4" />
        </Button>
      </div>
    );
  },
);
